'use client'

import { useEffect } from 'react'

import { FormattedMessage } from 'react-intl'
import { styled } from 'styled-components'

import {
  centsToDollars,
  CurrencyInputCents,
  InputBordered,
} from '@b-stock/fe-core/bstock-react'

import { useTrackFilterUsage } from '../analytics'
import type { Range } from '../commonTypes'
import WithDynamicButton from '../WithDynamicApplyButton'
import useFreeEntryRange from './useFreeEntryRange'

const Container = styled.div`
  max-width: 11.75rem;
  display: grid;
  grid-template-columns: 50% 50%;
  grid-gap: 0.625rem 0.5rem;
`

const Error = styled.div`
  grid-column: 1 / 3;
`

const FreeEntryContainer = styled.div`
  & input {
    width: 100%;
  }
`

const CurrencyCentsRangeFilter = ({
  selected,
  onChange,
  onError,
  desktopScreen = true,
  currency,
  analyticsTitle,
}: {
  selected: Range
  onChange: (value: Range) => void
  desktopScreen?: boolean
  onError?: (value: boolean) => void
  currency: string
  analyticsTitle: string
}) => {
  const { freeEntryRange, setMin, setMax } = useFreeEntryRange({
    selected,
    onChange,
    desktopScreen,
  })

  const showResultBtnVisible =
    desktopScreen && freeEntryRange.some((val, i) => val !== selected[i])
  const invalidMax =
    freeEntryRange[1] !== null &&
    freeEntryRange[0] !== null &&
    freeEntryRange[1] < freeEntryRange[0]

  const trackFilterUsage = useTrackFilterUsage<{
    filter_title: string
    min_bid: number | null
    max_bid: number | null
  }>('filters')

  const handleShowResultsClick = () => {
    trackFilterUsage({
      filter_title: analyticsTitle,
      min_bid:
        freeEntryRange[0] === null ? null : centsToDollars(freeEntryRange[0]),
      max_bid:
        freeEntryRange[1] === null ? null : centsToDollars(freeEntryRange[1]),
    })

    onChange(freeEntryRange)
  }

  useEffect(() => {
    onError?.(invalidMax)
    // We want onError to get called whenever `invalidMax` changes.
    // onError is likely stable, but we don't strictly care if it isn't
    // If it ever wasn't stable, we wouldn't want to re-trigger the callback
    // every single render.
    // oxlint-disable-next-line react/exhaustive-deps
  }, [invalidMax])

  return (
    <WithDynamicButton
      buttonContent={
        <FormattedMessage id="Filters.numericRange.filterButton" />
      }
      isButtonVisible={showResultBtnVisible}
      onClick={handleShowResultsClick}
      isButtonDisabled={invalidMax}
    >
      <Container>
        <FreeEntryContainer>
          <CurrencyInputCents
            inputMode="decimal"
            label={<FormattedMessage id="Filters.numericRange.min" />}
            currencyCode={currency}
            valueInCents={freeEntryRange[0] ?? undefined}
            min={0}
            allowCents={false}
            allowNegative={false}
            handleValueChange={(value) => {
              setMin(value ?? null)
            }}
          />
        </FreeEntryContainer>

        <FreeEntryContainer>
          <CurrencyInputCents
            inputMode="decimal"
            label={<FormattedMessage id="Filters.numericRange.max" />}
            currencyCode={currency}
            valueInCents={freeEntryRange[1] ?? undefined}
            min={0}
            allowCents={false}
            allowNegative={false}
            handleValueChange={(value) => {
              setMax(value ?? null)
            }}
            error={invalidMax}
          />
        </FreeEntryContainer>

        {invalidMax ? (
          <Error>
            <InputBordered.ErrorMessage>
              <FormattedMessage id="Filters.BaseFreeEntryInput.hasError" />
            </InputBordered.ErrorMessage>
          </Error>
        ) : null}
      </Container>
    </WithDynamicButton>
  )
}

export default CurrencyCentsRangeFilter
